/* eslint-disable react-hooks/exhaustive-deps,import/no-unresolved */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import AccordionItem from '../../components/AccordionItem';
import BasicContainer from '../../components/BasicContainer';
import { currencies, exchange, TIER_LEVEL } from '../../utilities/constants';
import {
  formatNumber,
  formatNumberWithComma,
  formatNumberWithRounding,
} from '../../utilities/formatHelpers';
import { useLedgerFilteredWallets } from '../../utilities/sharedHooks/ledgerWallets';
import { useRate } from '../../utilities/sharedHooks/rates';
import { RATES_QUERY_KEY, USER_PREFERENCES } from '../../utilities/sharedQueries';
import AddAccount from '../Accounts/partials/AddAccount';
import { WALLETS_ALL } from '../Accounts/queries';
import {
  benefitsTiersBadgeColorsMap,
  featuresScreens,
  mainStakingCurrency,
  stakingTypes,
} from './constants';
import { useSelectStakingTypes } from './hooks';
import StakingFilters from './partials/Filters';
import StakingList from './partials/StakingList';
import {
  GET_ACTIVE_STAKING_AMOUNT_WITH_TIERS,
  GET_ALL_STAKINGS,
  GET_STAKING_TIERS,
} from './queries';
import { useUserAllowStakingFeature } from '../../utilities/sharedHooks/user';
import { SkeletonPageLoader } from '../../components';

const StakingDashboard = () => {
  const { t: translate } = useTranslation();

  const history = useHistory();

  const [selectedStakingType, setSelectedStakingType] = useSelectStakingTypes();

  const [isOpenedBenefitBox, setIsOpenedBenefitBox] = useState(false);

  const { data: wallets, refetch: refetchAllWallets } = useLedgerFilteredWallets(WALLETS_ALL, {
    enabled: false,
  });

  const {
    data: allStakings,
    refetch: refetchAllStaking,
    isLoading: isLoadingFetchStakings,
  } = useQuery(GET_ALL_STAKINGS, {
    enabled: false,
  });

  const {
    data: activeStakingAmountTiers,
    isLoading: isLoadingActiveStakingAmountTiers,
    refetch: refetchActiveStakingAmountTiers,
  } = useQuery(GET_ACTIVE_STAKING_AMOUNT_WITH_TIERS, {
    enabled: false,
  });

  const {
    data: stakingTiers,
    isLoading: isLoadingStakingTiers,
    refetch: refetchStakingTiers,
  } = useQuery(GET_STAKING_TIERS, {
    enabled: false,
  });

  const { data: rates } = useQuery(RATES_QUERY_KEY);

  const usdConvertRate = useRate(mainStakingCurrency, currencies.USD.fullName, true);

  const { data: userPref } = useQuery(USER_PREFERENCES);
  const fiatCurrencyCode = userPref?.displayCurrency || 'EUR';

  const mainStakingWallet = useMemo(() => {
    if (wallets) {
      return wallets?.find((w) => w.coin.toLowerCase() === mainStakingCurrency);
    }
    return {};
  }, [wallets]);

  const stakingAmount = useMemo(() => {
    if (allStakings && allStakings?.length > 0) {
      return allStakings
        .filter((item) => item.state === stakingTypes.ACTIVE)
        .reduce((acc, item) => acc + parseFloat(item.amount), 0);
    }
    return 0;
  }, [allStakings]);

  const fiatValues = useMemo(() => {
    const result = {
      stakingAmountInFiat: 0,
      stakingAmountChangeInFiat: 0,
    };
    if (rates && fiatCurrencyCode) {
      let rateData;
      let dayChange = 0;
      let fiatValue = 0;
      const stakingAmountValue =
        stakingAmount * currencies[mainStakingCurrency.toUpperCase()].conversion;

      if (rates[`${mainStakingCurrency?.toUpperCase()}${fiatCurrencyCode?.toUpperCase()}`]) {
        rateData = rates[`${mainStakingCurrency?.toUpperCase()}${fiatCurrencyCode?.toUpperCase()}`];
        dayChange = formatNumber(
          rateData?.pctChange1d || rateData?.pctChange24h,
          exchange.FIAT_VALUE
        );
        fiatValue = formatNumber(stakingAmountValue * rateData.ask, exchange.FIAT_VALUE);
      }

      result.stakingAmountInFiat = parseFloat(fiatValue);
      if (!Number.isNaN(parseFloat(dayChange))) {
        result.stakingAmountChangeInFiat = (dayChange / 100) * fiatValue;
      }

      result.stakingAmountInFiat = result.stakingAmountInFiat.toFixed(2);
      result.stakingAmountChangeInFiat = result.stakingAmountChangeInFiat.toFixed(2);
    }
    return result;
  }, [rates, fiatCurrencyCode, stakingAmount]);

  const nextTierLevel = useMemo(() => {
    if (
      !isLoadingStakingTiers &&
      !isLoadingActiveStakingAmountTiers &&
      stakingTiers &&
      activeStakingAmountTiers
    ) {
      if (!activeStakingAmountTiers?.userStakingTier) {
        return stakingTiers.find((level) => level.tier === TIER_LEVEL.ONE);
      }
      if (activeStakingAmountTiers?.userStakingTier?.tier !== TIER_LEVEL.FIVE) {
        const nextTierNumber = activeStakingAmountTiers?.userStakingTier?.tier?.replace('tier', '');
        // eslint-disable-next-line radix
        const nextTier = `tier${parseInt(nextTierNumber) + 1}`;

        return stakingTiers.find((level) => level.tier === nextTier);
      }
    }
    return {};
  }, [
    stakingTiers,
    activeStakingAmountTiers,
    isLoadingStakingTiers,
    isLoadingActiveStakingAmountTiers,
  ]);

  const needToStakeNextLevelValues = useMemo(() => {
    const result = {
      amount: 0,
      amountInFiat: 0,
    };

    if (activeStakingAmountTiers && nextTierLevel && nextTierLevel?.tier) {
      const nextLevelTierValueRquiredInUSD = nextTierLevel?.required_staked_bxx_value_in_usd;
      const nextLevelTierValueRequiredInBXX =
        nextTierLevel?.required_staked_bxx_value_in_usd / usdConvertRate;

      const userActiveStakedAmountInUSD = parseFloat(
        activeStakingAmountTiers?.activeStakedAmountValueInUsd
      );
      const userActiveStakedAmountInBXX = parseFloat(activeStakingAmountTiers?.activeStakedAmount);

      result.amountInFiat = formatNumberWithComma(
        formatNumber(
          nextLevelTierValueRquiredInUSD - userActiveStakedAmountInUSD,
          exchange.FIAT_VALUE
        )
      );

      result.amount = formatNumberWithComma(
        formatNumberWithRounding(
          nextLevelTierValueRequiredInBXX - userActiveStakedAmountInBXX,
          exchange.CRYPTO_VALUE
        )
      );
    }

    return result;
  }, [nextTierLevel, activeStakingAmountTiers, usdConvertRate]);

  const bxxAvailableBalance =
    mainStakingWallet?.available_balance *
    currencies[mainStakingCurrency?.toUpperCase()].conversion;

  const openStakingFlow = bxxAvailableBalance > 0;

  const isAllowStakingFeature = useUserAllowStakingFeature();

  useEffect(() => {
    if (isAllowStakingFeature === false) {
      history.push(`/`);
    }
  }, [isAllowStakingFeature]);

  useEffect(() => {
    refetchAllWallets();
    refetchAllStaking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refetchStakingTiers();
    refetchActiveStakingAmountTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStakingType]);

  const stakingNowHandler = () => {
    if (openStakingFlow) {
      history.push('/dashboard/staking/features', {
        initScreen: featuresScreens.CREATE_STAKING,
      });
    } else {
      history.push('/dashboard/staking/features', {
        initScreen: featuresScreens.ADD_FUNDS_OPTIONS,
      });
    }
  };

  const renderStakeNowBtn = () => (
    <div className="w-100 mt-4">
      <AddAccount
        noMobile
        label={translate('stake_now_btn_label')}
        subLabel={translate('stake_now_btn_sublabel')}
        onClick={stakingNowHandler}
        icon="STAKING"
        isImg
        imageStyle={{ width: '2.5rem', height: '2.5rem' }}
      />
    </div>
  );

  const renderBenefitsBox = () => (
    <>
      <AccordionItem
        className="d-block d-md-none bg-lighter mt-3 pe-3"
        arrowIcons
        arrowIconColor="text-shadow"
        boldHeader
        header={
          <div className="d-flex flex-row justify-content-start align-items-center">
            <p className="text-f5 text-dark">
              {translate('staking_benefit_box_current_tier_level_label')}
            </p>
            {activeStakingAmountTiers?.userStakingTier && (
              <div
                className={`${
                  benefitsTiersBadgeColorsMap[activeStakingAmountTiers?.userStakingTier?.tier]
                    .background
                } px-2 py-1 rounded-2 ms-2`}>
                <p
                  className={`text-uppercase text-f6 ${
                    benefitsTiersBadgeColorsMap[activeStakingAmountTiers?.userStakingTier?.tier]
                      .text
                  }`}
                  style={{ fontFamily: 'HM Alpha Mono' }}>
                  {activeStakingAmountTiers?.userStakingTier?.name}
                </p>
              </div>
            )}
          </div>
        }
        content={
          <>
            {activeStakingAmountTiers?.userStakingTier && (
              <div>
                <div className="mt-3">
                  <p className="text-f6 text-dark">
                    {translate('staking_benefit_box_cryptodraft_label')}
                  </p>
                  <p className="text-f6 text-shadow">
                    {translate('staking_benefit_box_cryptodraft_sublabel').replace(
                      '__LTV_INTEREST__',
                      activeStakingAmountTiers?.userStakingTier?.cryptodraft_ltv_interest_free
                    )}
                  </p>
                </div>
                <div className="mt-3">
                  <p className="text-f6 text-dark">
                    {translate('staking_benefit_box_stableloan_label')}
                  </p>
                  <p className="text-f6 text-shadow">
                    {activeStakingAmountTiers?.userStakingTier?.tier === TIER_LEVEL.FIVE &&
                      activeStakingAmountTiers?.userStakingTier?.stableloan_ltv_interest_free &&
                      translate('staking_benefit_box_stableloan_sublabel_tier_5')}

                    {activeStakingAmountTiers?.userStakingTier?.tier !== TIER_LEVEL.FIVE &&
                      activeStakingAmountTiers?.userStakingTier?.stableloan_ltv_interest_free &&
                      translate('staking_benefit_box_stableloan_sublabel').replace(
                        '__LTV_INTEREST__',
                        activeStakingAmountTiers?.userStakingTier?.stableloan_ltv_interest_free
                      )}

                    {!activeStakingAmountTiers?.userStakingTier?.stableloan_ltv_interest_free &&
                      translate('staking_no_benefits_label')}
                  </p>
                </div>
              </div>
            )}
            {activeStakingAmountTiers?.userStakingTier?.tier !== TIER_LEVEL.FIVE && (
              <div
                className={`bg-light p-3 rounded-extra ${
                  activeStakingAmountTiers?.userStakingTier ? 'mt-4' : ''
                }`}>
                <div className="d-flex flex-row justify-content-start align-items-center">
                  <p className="text-f5 text-dark">{translate('next_level_label')}</p>
                  <div
                    className={`${
                      nextTierLevel?.tier
                        ? benefitsTiersBadgeColorsMap[nextTierLevel?.tier].background
                        : ''
                    } px-2 py-1 rounded-2 ms-2`}>
                    <p
                      className={`text-uppercase text-f6 ${
                        nextTierLevel?.tier
                          ? benefitsTiersBadgeColorsMap[nextTierLevel?.tier].text
                          : ''
                      }`}
                      style={{ fontFamily: 'HM Alpha Mono' }}>
                      {nextTierLevel?.name}
                    </p>
                  </div>
                </div>
                <div className="mt-2">
                  <p className="text-shadow text-f6">{translate('need_to_stake_label')}</p>
                  <p className="text-dark text-f5">{`${
                    needToStakeNextLevelValues.amount
                  } ${mainStakingCurrency.toUpperCase()}`}</p>
                  <p className="text-shadow text-f6">{`≈${needToStakeNextLevelValues.amountInFiat} ${currencies.USD.fullName}`}</p>
                </div>
                <div className="mt-2">
                  <p className="text-f6 text-dark">
                    {translate('staking_benefit_box_cryptodraft_label')}
                  </p>
                  <p className="text-f6 text-shadow">
                    {translate('staking_benefit_box_cryptodraft_sublabel').replace(
                      '__LTV_INTEREST__',
                      nextTierLevel?.cryptodraft_ltv_interest_free
                    )}
                  </p>
                </div>
                <div className="mt-2">
                  <p className="text-f6 text-dark">
                    {translate('staking_benefit_box_stableloan_label')}
                  </p>
                  <p className="text-f6 text-shadow">
                    {nextTierLevel?.tier === TIER_LEVEL.FIVE &&
                      nextTierLevel?.stableloan_ltv_interest_free &&
                      translate('staking_benefit_box_stableloan_sublabel_tier_5')}

                    {nextTierLevel?.tier !== TIER_LEVEL.FIVE &&
                      nextTierLevel?.stableloan_ltv_interest_free &&
                      translate('staking_benefit_box_stableloan_sublabel').replace(
                        '__LTV_INTEREST__',
                        nextTierLevel?.stableloan_ltv_interest_free
                      )}

                    {!nextTierLevel?.stableloan_ltv_interest_free &&
                      translate('staking_no_benefits_label')}
                  </p>
                </div>
              </div>
            )}
          </>
        }
        onClick={() => setIsOpenedBenefitBox((prev) => !prev)}
        opened={isOpenedBenefitBox}
      />

      <div className="d-none d-md-block bg-lighter rounded-extra p-4 mt-4">
        {activeStakingAmountTiers?.userStakingTier && (
          <div>
            <div className="d-flex flex-row justify-content-start align-items-center">
              <p className="text-f5 text-dark">
                {translate('staking_benefit_box_current_tier_level_label')}
              </p>
              <div
                className={`${
                  benefitsTiersBadgeColorsMap[activeStakingAmountTiers?.userStakingTier?.tier]
                    .background
                } px-2 py-1 rounded-2 ms-2`}>
                <p
                  className={`text-uppercase text-f6 ${
                    benefitsTiersBadgeColorsMap[activeStakingAmountTiers?.userStakingTier?.tier]
                      .text
                  }`}
                  style={{ fontFamily: 'HM Alpha Mono' }}>
                  {activeStakingAmountTiers?.userStakingTier?.name}
                </p>
              </div>
            </div>
            <div className="mt-3">
              <p className="text-f6 text-dark">
                {translate('staking_benefit_box_cryptodraft_label')}
              </p>
              <p className="text-f6 text-shadow">
                {translate('staking_benefit_box_cryptodraft_sublabel').replace(
                  '__LTV_INTEREST__',
                  activeStakingAmountTiers?.userStakingTier?.cryptodraft_ltv_interest_free
                )}
              </p>
            </div>
            <div className="mt-3">
              <p className="text-f6 text-dark">
                {translate('staking_benefit_box_stableloan_label')}
              </p>
              <p className="text-f6 text-shadow">
                {activeStakingAmountTiers?.userStakingTier?.tier === TIER_LEVEL.FIVE &&
                  activeStakingAmountTiers?.userStakingTier?.stableloan_ltv_interest_free &&
                  translate('staking_benefit_box_stableloan_sublabel_tier_5')}

                {activeStakingAmountTiers?.userStakingTier?.tier !== TIER_LEVEL.FIVE &&
                  activeStakingAmountTiers?.userStakingTier?.stableloan_ltv_interest_free &&
                  translate('staking_benefit_box_stableloan_sublabel').replace(
                    '__LTV_INTEREST__',
                    activeStakingAmountTiers?.userStakingTier?.stableloan_ltv_interest_free
                  )}

                {!activeStakingAmountTiers?.userStakingTier?.stableloan_ltv_interest_free &&
                  translate('staking_no_benefits_label')}
              </p>
            </div>
          </div>
        )}
        {!activeStakingAmountTiers?.userStakingTier && (
          <p className="text-f5 text-dark text-bold mb-3">
            {translate('staking_benefit_box_current_tier_level_label')}
          </p>
        )}
        {activeStakingAmountTiers?.userStakingTier?.tier !== TIER_LEVEL.FIVE && (
          <div
            className={`bg-light p-3 rounded-extra ${
              activeStakingAmountTiers?.userStakingTier ? 'mt-4' : ''
            }`}>
            <div className="d-flex flex-row justify-content-start align-items-center">
              <p className="text-f5 text-dark">{translate('next_level_label')}</p>
              <div
                className={`${
                  nextTierLevel?.tier
                    ? benefitsTiersBadgeColorsMap[nextTierLevel?.tier].background
                    : ''
                } px-2 py-1 rounded-2 ms-2`}>
                <p
                  className={`text-uppercase text-f6 ${
                    nextTierLevel?.tier ? benefitsTiersBadgeColorsMap[nextTierLevel?.tier].text : ''
                  }`}
                  style={{ fontFamily: 'HM Alpha Mono' }}>
                  {nextTierLevel?.name}
                </p>
              </div>
            </div>
            <div className="mt-2">
              <p className="text-shadow text-f6">{translate('need_to_stake_label')}</p>
              <p className="text-dark text-f5">{`${
                needToStakeNextLevelValues.amount
              } ${mainStakingCurrency.toUpperCase()}`}</p>
              <p className="text-shadow text-f6">{`≈${needToStakeNextLevelValues.amountInFiat} ${currencies.USD.fullName}`}</p>
            </div>
            <div className="mt-2">
              <p className="text-f6 text-dark">
                {translate('staking_benefit_box_cryptodraft_label')}
              </p>
              <p className="text-f6 text-shadow">
                {translate('staking_benefit_box_cryptodraft_sublabel').replace(
                  '__LTV_INTEREST__',
                  nextTierLevel?.cryptodraft_ltv_interest_free
                )}
              </p>
            </div>
            <div className="mt-2">
              <p className="text-f6 text-dark">
                {translate('staking_benefit_box_stableloan_label')}
              </p>
              <p className="text-f6 text-shadow">
                {nextTierLevel?.tier === TIER_LEVEL.FIVE &&
                  nextTierLevel?.stableloan_ltv_interest_free &&
                  translate('staking_benefit_box_stableloan_sublabel_tier_5')}

                {nextTierLevel?.tier !== TIER_LEVEL.FIVE &&
                  nextTierLevel?.stableloan_ltv_interest_free &&
                  translate('staking_benefit_box_stableloan_sublabel').replace(
                    '__LTV_INTEREST__',
                    nextTierLevel?.stableloan_ltv_interest_free
                  )}

                {!nextTierLevel?.stableloan_ltv_interest_free &&
                  translate('staking_no_benefits_label')}
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );

  const showBenefitsBox = !isLoadingActiveStakingAmountTiers && !isLoadingStakingTiers;

  if (isLoadingStakingTiers || isLoadingActiveStakingAmountTiers || isLoadingFetchStakings) {
    return <SkeletonPageLoader />;
  }

  return (
    <div className="container-xl">
      <div className="row mt-5 staking-container">
        <div className="col-md-3 col-sm-12">
          <div className="d-none d-md-block">
            <BasicContainer className="bg-lighter mb-3">
              <p className="text-f6 text-shadow text-bold">
                {translate('total_staking_balance_label')}
              </p>
              <p className="text-f4 mt-2">
                {`${formatNumberWithComma(
                  formatNumber(
                    stakingAmount * currencies[mainStakingCurrency.toUpperCase()].conversion,
                    exchange.CRYPTO_VALUE
                  )
                )} ${mainStakingCurrency.toUpperCase()}`}
              </p>
              <div className="d-flex flex-row mt-2">
                <p className="text-f6 text-shadow pe-2">{`≈${formatNumberWithComma(
                  fiatValues?.stakingAmountInFiat
                )} ${fiatCurrencyCode}`}</p>
                <p
                  className={`text-f6 ${
                    fiatValues?.stakingAmountChangeInFiat >= 0 ? 'text-success' : 'text-danger'
                  }`}>
                  {fiatValues?.stakingAmountChangeInFiat >= 0 ? '+' : ''}
                  {fiatValues?.stakingAmountChangeInFiat} {fiatCurrencyCode} (24h.)
                </p>
              </div>
            </BasicContainer>
          </div>
          <div className="d-block d-md-none">
            <div className="d-flex flex-row justify-content-between align-items-center">
              <p className="text-dark text-f3">{translate('staking_tab_label')}</p>
            </div>
            <p className="text-f6 text-shadow text-bold mt-4">
              {translate('total_staking_balance_label')}
            </p>
            <p className="text-f4 mt-2">
              {`${formatNumberWithComma(
                formatNumber(
                  stakingAmount * currencies[mainStakingCurrency.toUpperCase()].conversion,
                  exchange.CRYPTO_VALUE
                )
              )} ${mainStakingCurrency.toUpperCase()}`}
            </p>
            <div className="d-flex flex-row mt-2">
              <p className="text-f6 text-shadow pe-2">{`≈${formatNumberWithComma(
                fiatValues?.stakingAmountInFiat
              )} ${fiatCurrencyCode}`}</p>
              <p
                className={`text-f6 ${
                  fiatValues?.stakingAmountChangeInFiat >= 0 ? 'text-success' : 'text-danger'
                }`}>
                {fiatValues?.stakingAmountChangeInFiat >= 0 ? '+' : ''}
                {fiatValues?.stakingAmountChangeInFiat} {fiatCurrencyCode} (24h.)
              </p>
            </div>
          </div>
          {renderStakeNowBtn()}
          {showBenefitsBox && renderBenefitsBox()}
        </div>
        <div className="col-12 col-md-9 pe-0 staking-list-main-container">
          <StakingFilters onChangeType={setSelectedStakingType} selectedTab={selectedStakingType} />
          <StakingList
            list={allStakings}
            type={selectedStakingType}
            isLoading={isLoadingFetchStakings}
          />
        </div>
      </div>
    </div>
  );
};

export default StakingDashboard;
